import type { MosaicNode } from "react-mosaic-component";
import { getNextDefaultName } from "@/shared/helpers/getDefaultName/getDefaultName";
import type { ViewId } from "@/shared/types/common";
import { predefinedTabValues } from "./defaultTabValues";
import { TabsStateService } from "./TabsStateService";
import type { TabState, Tabs, TabsState, WidgetState, Widgets } from "./types";

export class TabsStateProvider {
	private static instance: TabsStateProvider;
	private static defaultInstance: TabsStateProvider;
	private tabsState: TabsState;
	private activeTabId: string;
	private isDirty: boolean;
	private readonly interval?: NodeJS.Timeout;

	private constructor(
		private readonly userId: string | undefined,
		state: Omit<Tabs, "userId">,
	) {
		const tabsStateService = TabsStateService.create();

		this.tabsState = [
			...predefinedTabValues,
			...(state?.tabs ?? []).filter((tab) => !tab.isDefault),
		];
		this.activeTabId =
			this.tabsState.find((tab) => tab.id === state?.activeTabId)?.id ??
			this.tabsState[0].id;
		this.isDirty = false;

		if (userId) {
			this.interval = setInterval(async () => {
				if (this.isDirty && userId) {
					await tabsStateService.setData(userId, {
						userId: userId,
						activeTabId: this.activeTabId,
						tabs: this.tabsState.filter((tab) => !tab.isDefault),
					});
					this.isDirty = false;
				}
			}, 10000);
		}
	}

	private clear() {
		clearInterval(this.interval);
	}

	static getDefault() {
		if (TabsStateProvider.instance) {
			return TabsStateProvider.instance;
		}
		if (!TabsStateProvider.defaultInstance) {
			TabsStateProvider.defaultInstance = new TabsStateProvider(undefined, {
				tabs: predefinedTabValues,
			});
		}
		return TabsStateProvider.defaultInstance;
	}

	static async getProvider(userId?: string) {
		if (!userId) return TabsStateProvider.getDefault();
		if (
			!TabsStateProvider.instance ||
			userId !== TabsStateProvider.instance.userId
		) {
			TabsStateProvider.instance?.clear();
			const tabsData = (await TabsStateService.create().getData(userId)).data;
			TabsStateProvider.instance = new TabsStateProvider(
				userId,
				tabsData ?? { tabs: predefinedTabValues },
			);
		}

		if (TabsStateProvider.instance.shouldResetState() === true) {
			TabsStateProvider.instance.resetState(userId);
		}

		return TabsStateProvider.instance;
	}

	private shouldResetState(): boolean {
		return false;
	}

	resetState(userId: string) {
		TabsStateService.create().removeData(userId);
		this.setState(predefinedTabValues);
	}

	getState(): Omit<Tabs, "userId"> {
		return { tabs: this.tabsState, activeTabId: this.activeTabId };
	}

	private setState(state: TabsState): void {
		this.tabsState = state;
		this.isDirty = true;
	}

	setActiveTab(tabId: string): void {
		if (this.activeTabId === tabId) return;

		this.activeTabId = tabId;
		this.isDirty = true;
	}

	getActiveTab(): string {
		return this.activeTabId;
	}

	setTabName(tabId: string, newName: string): void {
		const state = this.tabsState.map((tab) => ({
			...tab,
			name: tab.id === tabId ? newName : tab.name,
		}));
		this.setState(state);
	}

	setTabLayout(layout: MosaicNode<ViewId> | null): void {
		const state = this.tabsState.map((tab) =>
			tab.id === this.activeTabId ? { ...tab, layout } : tab,
		);

		this.setState(state);
	}

	getTabLayout(): MosaicNode<ViewId> | null {
		return (
			this.tabsState.find((tab) => tab.id === this.activeTabId)?.layout ?? null
		);
	}

	setTabWidgetsState(widgets: Widgets): void {
		const state = this.tabsState.map((tab) =>
			tab.id === this.activeTabId ? { ...tab, widgets } : tab,
		);
		this.setState(state);
	}

	getTabWidgetsState(): Widgets {
		return (
			this.tabsState.find((tab) => tab.id === this.activeTabId)?.widgets ?? {}
		);
	}

	setWidgetState(widgetId: ViewId, data: Partial<WidgetState>): void {
		const state = this.tabsState.map((tab) =>
			tab.id === this.activeTabId
				? {
						...tab,
						widgets: {
							...tab.widgets,
							[widgetId]: { ...tab.widgets?.[widgetId], ...data },
						},
					}
				: tab,
		);
		this.setState(state);
	}

	getWidgetState(widgetId: ViewId): WidgetState | undefined {
		return this.tabsState.find((tab) => tab.id === this.activeTabId)?.widgets?.[
			widgetId
		];
	}

	addTab(): string {
		const newName = getNextDefaultName(
			"Dashboard",
			this.tabsState.map((tab) => tab.name),
		);
		const newId = getNextDefaultName(
			"Dashboard",
			this.tabsState.map((tab) => tab.id),
		);

		const newTab: TabState = {
			id: newId,
			name: newName,
			layout: predefinedTabValues[1].layout,
			widgets: predefinedTabValues[1].widgets,
		};

		this.activeTabId = newId;
		this.setState([...this.tabsState, newTab]);
		return newName;
	}

	closeTab(tabId: string): void {
		const updatedTabs = this.tabsState.filter((tab) => tab.id !== tabId);

		// if there isn't any active tab, set the first one as active
		if (this.activeTabId === tabId) {
			this.activeTabId = updatedTabs[0].id;
		}

		this.setState(updatedTabs);
	}
}
